import React, { useState, useEffect, useRef } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { Loader2, BarChart3, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { getForecastMapData, ForecastMapData, FORECAST_SEARCH_IDS } from "@/services/api/forecast";
import { cn } from "@/lib/utils";
import * as echarts from 'echarts';
import { Typography, Select, Input } from "antd";

// 生成冷色调随机颜色（绿、棕黄、蓝、紫）
const generateRandomColor = (): string => {
  const colors = [
    // 绿色系
    '#10B981', // 翠绿
    '#14B8A6', // 青绿
    '#059669', // 深绿
    '#0D9488', // 深青绿
    // 棕黄色系
    '#F59E0B', // 琥珀黄
    '#D97706', // 深黄
    '#B45309', // 棕黄
    '#92400E', // 深棕
    // 蓝色系
    '#3B82F6', // 蓝色
    '#2563EB', // 深蓝
    '#1D4ED8', // 靛蓝
    '#1E40AF', // 深靛蓝
    // 紫色系
    '#8B5CF6', // 紫色
    '#7C3AED', // 深紫
    '#6D28D9', // 紫罗兰
    '#5B21B6', // 深紫罗兰
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// 品牌选项接口
interface BrandOption {
  code: string;
  name: string;
} 

// 品牌数据源
const BRAND_OPTIONS: BrandOption[] = [
  { code: 'B2214', name: 'KIMTRUE且初' },
  { code: 'B00206', name: 'EVERBAB艾蓓拉' },
  { code: 'B02330', name: 'ddg' },
  { code: 'B02333', name: '3odice三蒂丝' },
  { code: 'B02335', name: 'ONCUR安修泽' },
  { code: 'B02407', name: 'Aucuz奥札' },
];
const MapPredict: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [mapData, setMapData] = useState<ForecastMapData[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chart2ProductCount, setChart2ProductCount] = useState(0); // 第二个图表的产品数量
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const chart2Ref = useRef<HTMLDivElement>(null);
  const chart2Instance = useRef<echarts.ECharts | null>(null);
  const productColors = useRef<Map<string, string>>(new Map());
  // 品牌选择相关状态
  const [selectedBrandCode, setSelectedBrandCode] = useState<string>('B2214'); // 默认选择B2214
  // 产品编码搜索状态
  const [selectedProductCode, setSelectedProductCode] = useState<string>(''); // 产品编码搜索
  // 品牌选择处理函数
  const handleBrandChange = (brandCode: string) => {
    console.log('品牌选择变化:', brandCode);

    // 完全销毁第一个图表
    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    // 完全销毁第二个图表
    if (chart2Instance.current) {
      chart2Instance.current.dispose();
      chart2Instance.current = null;
    }

    // 重置第二个图表容器的宽度和内容
    if (chart2Ref.current) {
      chart2Ref.current.style.width = '100%';
      chart2Ref.current.style.minWidth = '100%';
      chart2Ref.current.innerHTML = '';
    }

    // 重置第一个图表容器
    if (chartRef.current) {
      chartRef.current.innerHTML = '';
    }

    // 清空产品颜色映射
    productColors.current.clear();

    // 清空当前数据
    setMapData([]);

    // 更新品牌选择
    setSelectedBrandCode(brandCode);
  };

  // 切换全屏模式
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

   // 产品编码搜索处理函数
   const handleProductCodeChange = (productCode: string) => {
    console.log('产品编码搜索变化:', productCode);

    // 完全销毁第一个图表
    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    // 完全销毁第二个图表
    if (chart2Instance.current) {
      chart2Instance.current.dispose();
      chart2Instance.current = null;
    }

    // 重置第二个图表容器的宽度和内容
    if (chart2Ref.current) {
      chart2Ref.current.style.width = '100%';
      chart2Ref.current.style.minWidth = '100%';
      chart2Ref.current.innerHTML = '';
    }

    // 重置第一个图表容器
    if (chartRef.current) {
      chartRef.current.innerHTML = '';
    }

    // 清空产品颜色映射
    productColors.current.clear();

    // 清空当前数据
    setMapData([]);

    // 更新产品编码
    setSelectedProductCode(productCode);
  };

  // 缩放控制
  const zoomIn = () => {
    if (chartInstance.current) {
      const option = chartInstance.current.getOption() as any;
      const dataZoom = option.dataZoom || [];
      // 适配 inside 类型的 dataZoom
      const insideZoom = dataZoom.find((dz: any) => dz.type === 'inside');
      if (insideZoom) {
        const currentStart = insideZoom.start || 0;
        const currentEnd = insideZoom.end || 100;
        const range = currentEnd - currentStart;
        const newRange = range * 0.8; // 缩小范围，相当于放大
        const center = (currentStart + currentEnd) / 2;
        const newStart = Math.max(0, center - newRange / 2);
        const newEnd = Math.min(100, center + newRange / 2);
        
        chartInstance.current.setOption({
          dataZoom: [{
            type: 'inside',
            start: newStart,
            end: newEnd
          }]
        });
      }
    }
  };

  const zoomOut = () => {
    if (chartInstance.current) {
      const option = chartInstance.current.getOption() as any;
      const dataZoom = option.dataZoom || [];
      // 适配 inside 类型的 dataZoom
      const insideZoom = dataZoom.find((dz: any) => dz.type === 'inside');
      if (insideZoom) {
        const currentStart = insideZoom.start || 0;
        const currentEnd = insideZoom.end || 100;
        const range = currentEnd - currentStart;
        const newRange = Math.min(100, range * 1.25); // 扩大范围，相当于缩小
        const center = (currentStart + currentEnd) / 2;
        const newStart = Math.max(0, center - newRange / 2);
        const newEnd = Math.min(100, center + newRange / 2);
        
        chartInstance.current.setOption({
          dataZoom: [{
            type: 'inside',
            start: newStart,
            end: newEnd
          }]
        });
      }
    }
  };

  const resetZoom = () => {
    if (chartInstance.current) {
      chartInstance.current.setOption({
        dataZoom: [{
          type: 'inside',
          start: 0,
          end: 100
        }]
      });
    }
  };

    // 初始化和品牌/产品编码变化时获取数据
    useEffect(() => {
      fetchMapData();
    }, [selectedBrandCode, selectedProductCode]); // 当品牌或产品编码选择变化时重新获取数据

  // 初始化和品牌变化时获取数据
  useEffect(() => {
    fetchMapData();
  }, [selectedBrandCode]); // 当品牌选择变化时重新获取数据
  // 获取预测MAPE数据
  const fetchMapData = async () => {
    setLoading(true);
    // 清空之前的产品颜色映射，每次品牌切换时重新分配颜色
    productColors.current.clear();

    try {
      const response = await getForecastMapData({
        search_id: FORECAST_SEARCH_IDS.FORECAST_MAP_STATUS,
        page_size: 5000,
        page_no: 1,
        brand_code: selectedBrandCode,
        product_code: selectedProductCode, // 添加产品编码参数
      });

      console.log('API响应:', response);

      if (response.res_status_code === '0' && response.res_content.data_list) {
        let dataList: ForecastMapData[] = [];

        // 尝试从不同位置获取数据
        dataList = response.res_content.data_list;

        console.log('解析到的MAP数据:', dataList);

        if (dataList.length > 0) {
          // 为每个产品编码分配颜色（品牌切换后重新分配）
          const products = [...new Set(dataList.map(item => item.产品编码))];
          console.log('当前品牌产品数量:', products.length);
          console.log('产品编码:', products);

          products.forEach(product => {
            // productColors已在fetchMapData开始时清空，这里直接分配新颜色
            productColors.current.set(product, generateRandomColor());
          });

          setMapData(dataList);
        } else {
          console.warn('API返回成功但没有数据');
          setMapData([]);
        }
      } else {
        console.error('API返回非成功状态:', response);
        setMapData([]);
      }
    } catch (error) {
      console.error('获取预测MAPE数据失败:', error);
      toast({
        title: "错误",
        description: "获取数据失败，请稍后重试",
        variant: "destructive",
      });
      setMapData([]);
    } finally {
      setLoading(false);
    }
  };

  // 更新图表
  const updateChart = () => {
    if (!chartRef.current || mapData.length === 0) return;

    // 如果图表实例已存在，先销毁
    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    // 创建新的图表实例
    chartInstance.current = echarts.init(chartRef.current);

    // 获取所有唯一的月份并排序
    const months = [...new Set(mapData.map(item => item.月份))].sort();
    const monthIndexMap = new Map<string, number>();
    months.forEach((month, index) => {
      monthIndexMap.set(month, index);
    });

    // 按产品编码分组数据，过滤掉MAPE > 5 或 MAPE < 0的异常数据，以及不在月份范围内的数据
    const productDataMap = new Map<string, Array<[number, number]>>();
    console.log('mapData', mapData);
    let filteredCount = 0;
    let monthFilteredCount = 0;

    mapData.forEach(item => {
      const product = item.产品编码;
      const mape = typeof item.MAPE === 'number' ? item.MAPE : parseFloat(String(item.MAPE)) || 0;


      // 过滤掉MAPE大于5、小于0或等于0的数据
      if (mape > 5 || mape < 0 || mape === 0) {
        filteredCount++;
        console.warn(`过滤掉异常数据 - 产品: ${product}, 月份: ${item.月份}, MAPE: ${mape}`);
        return;
      }

      if (!productDataMap.has(product)) {
        productDataMap.set(product, []);
      }
      // 将月份转换为索引，MAPE作为Y值
      const monthIndex = monthIndexMap.get(item.月份) || 0;
      productDataMap.get(product)!.push([monthIndex, mape]);
    });

    if (monthFilteredCount > 0) {
      console.log(`共过滤掉 ${monthFilteredCount} 条月份范围外数据（不在2025-01至2026-01之间）`);
    }
    if (filteredCount > 0) {
      console.log(`共过滤掉 ${filteredCount} 条异常数据（MAPE > 5 或 MAPE ≤ 0）`);
    }

    // 构建系列数据 - 每个产品使用散点图
    const series = Array.from(productDataMap.entries()).map(([product, data]) => {
      // 按月份索引排序
      data.sort((a, b) => a[0] - b[0]);
      const productColor = productColors.current.get(product) || generateRandomColor();

      // 为每个数据点添加标签显示MAPE数值
      const dataPoints = data.map(([monthIndex, mape]) => ({
        value: [monthIndex, mape],
      }));

      return {
        name: product,
        type: 'scatter',
        data: dataPoints,
        // 根据数据量决定是否启用大数据量优化
        // 如果数据点太多，启用优化但标签可能不显示；如果数据点适中，禁用优化确保标签显示
        large: dataPoints.length > 500, // 只有数据点超过500时才启用large模式
        largeThreshold: 500,
        // 采样策略：数据多时使用采样，但会保留标签显示
        sampling: dataPoints.length > 500 ? 'lttb' : false, // 数据多时采样，少时不采样
        symbolSize: () => {
          // 散点小一点
          return 7;
        },
        itemStyle: {
          color: productColor,
          borderColor: '#fff',
          borderWidth: 1,
          opacity: 0.7, // 低透明度
        },
        label: {
          show: true, // 显示散点标签
          position: 'right', // 在散点右边显示
          formatter: (params: any) => {
            // 显示MAPE数值，保留四位小数
            if (!params) {
              return '';
            }
            // 处理不同的数据格式
            let mape: number | null = null;
            if (params.value && Array.isArray(params.value) && params.value.length >= 2) {
              mape = params.value[1];
            } else if (params.data && typeof params.data === 'object' && params.data.value) {
              mape = params.data.value[1];
            }
            
            if (mape === null || mape === undefined || isNaN(mape)) {
              return '';
            }
            return Number(mape).toFixed(4); // 显示四位小数
          },
          fontSize: 9, // 稍微减小字体以适应更多标签
          color: productColor,
          fontWeight: 'normal',
          distance: 5, // 标签与散点的距离
        },
        // 标签布局避让 - 使用更有效的避让策略
        labelLayout: {
          hideOverlap: true, // 隐藏完全重叠的标签（数据多时只显示部分，避免拥挤）
          moveOverlap: 'shiftY', // 在Y轴方向移动标签以避免重叠
          draggable: false,
        },
        emphasis: {
          label: {
            show: false, // hover时也不显示标签，只通过tooltip显示
          },
          itemStyle: {
            borderWidth: 3,
            opacity: 0.9, // hover时提高透明度
            scale: true,
          },
        },
        z: 2,
      };
    });

    // 如果产品编码有值，添加绿色趋势折线
    if (selectedProductCode && productDataMap.has(selectedProductCode)) {
      const productData = productDataMap.get(selectedProductCode);
      if (productData && productData.length > 0) {
        // 按月份索引排序
        const sortedData = [...productData].sort((a, b) => a[0] - b[0]);
        const lineData = sortedData.map(([monthIndex, mape]) => [monthIndex, mape]);

        series.push({
          name: `${selectedProductCode} 趋势`,
          type: 'line',
          data: lineData,
          smooth: true,
          lineStyle: {
            color: '#10B981', // 绿色
            width: 3,
          },
          itemStyle: {
            color: '#10B981',
          },
          symbolSize: 10,
          label: {
            show: false,
          },
          z: 10, // 确保折线在最上层
        });
      }
    }

    const option = {
      title: {
        text: '',
        left: 'center',
        top: 20,
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold',
          color: '#333',
        },
      },
      tooltip: {
        trigger: 'axis', // 改为axis触发，显示轴线
        enterable: true, // 允许鼠标进入tooltip，使其可以滚动
        confine: true, // 将tooltip限制在图表容器内
        axisPointer: {
          type: 'line', // 显示垂直线
          lineStyle: {
            color: 'rgba(128, 128, 128, 0.3)', // 灰色透明点状线
            width: 1,
            type: 'dotted', // 点状线
          },
          label: {
            backgroundColor: 'rgba(128, 128, 128, 0.7)',
          },
        },
        formatter: (params: any) => {
          try {
            // 基础检查
            if (!params || !Array.isArray(params) || params.length === 0) {
              return '';
            }

            // 获取月份信息
            const firstParam = params[0];
            if (!firstParam) {
              return '';
            }

            const monthIndex = firstParam.dataIndex !== undefined ? firstParam.dataIndex : (firstParam.value && Array.isArray(firstParam.value) ? firstParam.value[0] : 0);
            const month = (monthIndex !== null && monthIndex !== undefined && months[monthIndex]) ? months[monthIndex] : '未知月份';

            // 过滤出散点图数据
            const scatterData = params.filter((param: any) => {
              return param.value && Array.isArray(param.value) && param.value.length >= 2;
            });

            if (scatterData.length === 0) {
              return `<div style="padding: 8px;"><div style="font-weight: bold;">${month}</div><div style="color: #999; margin-top: 4px;">暂无数据</div></div>`;
            }

            // 按MAPE值排序（从大到小）
            scatterData.sort((a: any, b: any) => {
              const mapeA = a.value[1];
              const mapeB = b.value[1];
              return mapeB - mapeA;
            });

            let html = `<div style="padding: 8px; min-width: 280px; max-width: 400px;">`;
            html += `<div style="font-weight: bold; margin-bottom: 8px; font-size: 14px; border-bottom: 2px solid #3B82F6; padding-bottom: 4px;">`;
            html += `${month} <span style="color: #666; font-size: 12px; font-weight: normal;">(${scatterData.length}个产品)</span>`;
            html += `</div>`;

            // 产品列表区域 - 添加最大高度和滚动，自定义滚动条样式
            html += `<div style="
              max-height: 500px;
              overflow-y: auto;
              overflow-x: hidden;
              padding-right: 8px;
              scrollbar-width: thin;
              scrollbar-color: #3B82F6 #E5E7EB;
            ">`;

            // 显示所有产品数据
            scatterData.forEach((param: any, index: number) => {
              const mape = param.value[1];
              const mapeNum = Number(mape);
              const isAbnormal = mapeNum > 0.5;
              const productName = param.seriesName || '未知产品';

              html += `<div style="margin-top: ${index === 0 ? 4 : 6}px; padding: 4px 6px; border-left: 3px solid ${param.color}; background: ${isAbnormal ? 'rgba(239, 68, 68, 0.05)' : 'rgba(59, 130, 246, 0.05)'}; border-radius: 2px;">`;
              html += `<div style="display: flex; justify-content: space-between; align-items: center;">`;
              html += `<div style="font-weight: 600; color: ${param.color}; font-size: 12px; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 250px;" title="${productName}">${productName}</div>`;
              html += `<div style="margin-left: 8px; color: ${isAbnormal ? '#EF4444' : '#3B82F6'}; font-weight: bold; font-size: 13px; white-space: nowrap;">`;
              html += `${mape}`; // 显示完整MAPE值，不使用toFixed
              // html += `${mapeNum.toFixed(4)}`;
              if (isAbnormal) {
                html += ` <span style="color: #EF4444; font-size: 11px;">⚠️</span>`;
              }
              html += `</div>`;
              html += `</div>`;
              html += `</div>`;
            });

            html += `</div>`; // 关闭滚动区域
            html += `</div>`; // 关闭外层容器
            return html;
          } catch (error) {
            console.error('Tooltip formatter error:', error);
            return '';
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#3B82F6',
        borderWidth: 2,
        textStyle: {
          color: '#333',
        },
        padding: [12, 16],
        extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 8px; user-select: text; cursor: text;',
      },
      legend: {
        data: selectedProductCode && productDataMap.has(selectedProductCode)
          ? [...Array.from(productDataMap.keys()), `${selectedProductCode} 趋势`]
          : Array.from(productDataMap.keys()),
        top: 50,
        type: 'scroll',
        orient: 'horizontal',
        left: 'center',
        itemGap: 8, // 减小间距
        itemWidth: 10, // 减小图标宽度
        itemHeight: 10, // 减小图标高度
        textStyle: {
          fontSize: 10, // 减小字体大小
        },
        selectedMode: true, // 允许点击图例切换显示/隐藏
      },
      grid: {
        left: '3%',
        right: '3%',
        bottom: '3%', // 去掉底部滚动条后减少底部空间
        top: '15%',
        containLabel: true,
      },
      // X轴和Y轴缩放功能
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: [0], // 作用于X轴
          start: 0,
          end: 100,
          zoomOnMouseWheel: false, // 鼠标滚轮缩放X轴
          moveOnMouseMove: false, // 鼠标移动平移
          moveOnMouseWheel: false,
        },
        {
          type: 'inside',
          yAxisIndex: [0], // 作用于Y轴，支持鼠标滚轮缩放
          start: 0,
          end: 100,
          zoomOnMouseWheel: false, // 鼠标滚轮缩放Y轴
          moveOnMouseMove: false, // Y轴不通过鼠标移动平移
          moveOnMouseWheel: false,
        },
        {
          type: 'slider',
          yAxisIndex: [0], // 作用于Y轴
          right: 10, // 右侧位置
          width: 20, // 滑块宽度
          start: 0, // 初始显示范围 0-100%
          end: 100,
          handleIcon: 'path://M30.9,53.2C16.8,53.2,5.3,41.7,5.3,27.6S16.8,2,30.9,2C45,2,56.4,13.5,56.4,27.6S45,53.2,30.9,53.2z M30.9,3.5C17.6,3.5,6.8,14.4,6.8,27.6c0,13.3,10.8,24.1,24.1,24.1C44.2,51.7,55,40.9,55,27.6C54.9,14.4,44.1,3.5,30.9,3.5z M36.9,35.8c0,0.6-0.4,1-1,1H26.8c-0.6,0-1-0.4-1-1V19.5c0-0.6,0.4-1,1-1h9.2c0.6,0,1,0.4,1,1V35.8z',
          handleSize: '80%',
          handleStyle: {
            color: '#3B82F6',
            shadowBlur: 3,
            shadowColor: 'rgba(0, 0, 0, 0.3)',
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
          textStyle: {
            color: '#666',
            fontSize: 11,
          },
          borderColor: '#ccc',
          fillerColor: 'rgba(59, 130, 246, 0.2)', // 选中区域填充色
          dataBackground: {
            lineStyle: {
              color: '#3B82F6',
              width: 1,
            },
            areaStyle: {
              color: 'rgba(59, 130, 246, 0.1)',
            },
          },
          selectedDataBackground: {
            lineStyle: {
              color: '#3B82F6',
              width: 2,
            },
            areaStyle: {
              color: 'rgba(59, 130, 246, 0.3)',
            },
          },
        },
        {
          type: 'inside',
          yAxisIndex: [0], // Y轴内部缩放（鼠标滚轮）
          zoomOnMouseWheel: false, // 鼠标滚轮缩放Y轴
          moveOnMouseMove: false, // Y轴不支持拖拽平移1.1
          moveOnMouseWheel: true, // 鼠标滚轮平移Y轴
        },
      ],
      xAxis: {
        type: 'category',
        name: '月份',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontSize: 14,
          fontWeight: 'bold',
        },
        data: months,
        axisLabel: {
          rotate: 45,
          fontSize: 11,
          interval: 0, // 显示所有标签
          formatter: (value: string) => {
            // 格式化月份显示，如果是日期格式则提取月份
            if (value.includes('/')) {
              const parts = value.split('/');
              if (parts.length >= 2) {
                return `${parts[0]}/${parts[1]}`;
              }
            }
            return value;
          },
        },
        axisLine: {
          lineStyle: {
            color: '#666',
          },
        },
      },
      yAxis: {
        type: 'value',
        name: 'MAPE',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          fontSize: 14,
          fontWeight: 'bold',
        },
        min: 0,
        max: 5,
        interval: 0.5, // Y轴分隔间隔为0.5
        axisLabel: {
          formatter: (value: number) => value.toFixed(1),
          fontSize: 12,
        },
        axisLine: {
          lineStyle: {
            color: '#666',
          },
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#E5E7EB',
          },
        },
        // 添加异常阈值线
        markLine: {
          silent: true,
          lineStyle: {
            color: '#EF4444',
            type: 'dashed',
            width: 2,
          },
          label: {
            show: true,
            position: 'end',
            formatter: '异常阈值 (0.5)',
            color: '#EF4444',
            fontSize: 12,
            fontWeight: 'bold',
          },
          data: [
            {
              yAxis: 0.5,
              name: '异常阈值',
            },
          ],
        },
        // 标记异常区域
        markArea: {
          silent: true,
          itemStyle: {
            color: 'rgba(239, 68, 68, 0.03)', // 降低透明度，更柔和
            borderColor: 'rgba(239, 68, 68, 0.1)',
            borderWidth: 1,
          },
          data: [
            [
              {
                yAxis: 0.5,
                name: '异常区域',
              },
              {
                yAxis: 'max',
              },
            ],
          ],
          label: {
            show: true,
            position: 'inside',
            formatter: '异常区域\n(MAPE > 0.5)',
            color: '#EF4444',
            fontSize: 11,
            fontWeight: 'bold',
          },
        },
      },
      series: series,
    };

    chartInstance.current.setOption(option);

    // 监听窗口大小变化
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  };

  // 更新第二个图表 - M+2预测情况
  const updateChart2 = () => {
    if (!chart2Ref.current || mapData.length === 0) return;

    // 如果图表实例已存在，先销毁
    if (chart2Instance.current) {
      chart2Instance.current.dispose();
      chart2Instance.current = null;
    }

    // 按产品编码分组，计算每个产品的统计数据
    const productStatsMap = new Map<string, { totalMape: number; monthCount: number; highMapeCount: number }>();

    mapData.forEach(item => {
      const product = item.产品编码;
      const mape = typeof item.MAPE === 'number' ? item.MAPE : parseFloat(String(item.MAPE)) || 0;

      // 过滤掉MAPE大于5、小于0或等于0的数据
      if (mape > 5 || mape < 0 || mape === 0) {
        return;
      }

      if (!productStatsMap.has(product)) {
        productStatsMap.set(product, {
          totalMape: 0,
          monthCount: 0,
          highMapeCount: 0,
        });
      }

      const stats = productStatsMap.get(product)!;
      stats.totalMape += mape;
      stats.monthCount += 1;
      if (mape > 0.6) {
        stats.highMapeCount += 1;
      }
    });

    // 转换为数组并排序（按平均MAPE降序）
    const productStats = Array.from(productStatsMap.entries())
      .map(([product, stats]) => ({
        product,
        avgMape: stats.monthCount > 0 ? stats.totalMape / stats.monthCount : 0,
        highMapeRatio: stats.monthCount > 0 ? stats.highMapeCount / stats.monthCount : 0,
      }))
      .sort((a, b) => b.avgMape - a.avgMape); // 按平均MAPE降序排序

    const products = productStats.map(item => item.product);
    const avgMapeData = productStats.map(item => item.avgMape);
    // 散点图数据：比例值（0-1范围），Y轴会显示为0-5
    const highMapeRatioData = productStats.map(item => item.highMapeRatio);
    
    // 调试信息：打印数据统计
    console.log('散点图数据准备:', {
      totalProducts: products.length,
      sampleAvgMape: avgMapeData.slice(0, 5),
      sampleRatio: highMapeRatioData.slice(0, 5),
    });

    // 计算图表宽度 - 根据产品数量动态计算
    const totalProducts = products.length;
    setChart2ProductCount(totalProducts); // 更新产品数量状态

    console.log(`当前品牌产品数量: ${totalProducts}`);

    // 固定参数 - 根据产品数量动态调整柱宽
    let barWidth: number;
    let barGap: number;

    if (totalProducts === 1) {
      // 单个产品：占据整个页面宽度
      barWidth = 150;
      barGap = 0;
    } else if (totalProducts <= 5) {
      // 2-5个产品：较宽的柱状图
      barWidth = 150;
      barGap = 50;
    } else if (totalProducts <= 20) {
      // 6-20个产品：中等宽度
      barWidth = 60;
      barGap = 20;
    } else {
      // 超过20个产品：紧凑显示
      barWidth = 20;
      barGap = 5;
    }

    const leftRightMargin = 200; // 左右边距总和（grid.left + grid.right）
    const minChartWidth = 1000; // 最小图表宽度

    // 计算图表总宽度：产品数量 * (柱宽 + 间距) + 左右边距
    const calculatedWidth = totalProducts * (barWidth + barGap) + leftRightMargin;
    const chartWidth = Math.max(minChartWidth, calculatedWidth);

    console.log(`产品数量: ${totalProducts}, 柱宽: ${barWidth}px, 间距: ${barGap}px, 最终宽度: ${chartWidth}px`);

    console.log(`计算的图表宽度: ${chartWidth}px`);

    // 先完全清空容器，确保没有残留内容
    if (chart2Ref.current) {
      chart2Ref.current.innerHTML = '';
      // 重置容器宽度，先设置为100%让浏览器重新计算
      chart2Ref.current.style.width = '100%';
      chart2Ref.current.style.minWidth = '100%';
      chart2Ref.current.style.maxWidth = 'none';
    }

    // 等待一个事件循环，让浏览器完成布局重置
    setTimeout(() => {
      if (!chart2Ref.current) return;

      // 动态设置图表容器宽度，让ECharts渲染出实际需要的宽度
      chart2Ref.current.style.width = `${chartWidth}px`;
      chart2Ref.current.style.minWidth = `${chartWidth}px`;
      chart2Ref.current.style.maxWidth = `${chartWidth}px`;

      // 强制浏览器重新计算布局
      chart2Ref.current.offsetHeight; // 触发重排
      const actualWidth = chart2Ref.current.offsetWidth;
      console.log(`设置宽度后，容器实际宽度: ${actualWidth}px, 期望宽度: ${chartWidth}px`);

      // 再等待一个事件循环，确保宽度设置生效
      requestAnimationFrame(() => {
        if (!chart2Ref.current) return;

        // 再次检查宽度，确保设置成功
        const finalWidth = chart2Ref.current.offsetWidth;
        if (Math.abs(finalWidth - chartWidth) > 10) {
          console.warn(`宽度设置失败，强制重新设置: ${chartWidth}px`);
          chart2Ref.current.style.width = `${chartWidth}px`;
          chart2Ref.current.style.minWidth = `${chartWidth}px`;
          chart2Ref.current.style.maxWidth = `${chartWidth}px`;
        }

        // 创建新的图表实例（在容器宽度更新后）
        // 每次都重新创建，确保使用最新的容器尺寸
        if (chart2Instance.current) {
          chart2Instance.current.dispose();
          chart2Instance.current = null;
        }
        chart2Instance.current = echarts.init(chart2Ref.current);

      const option2 = {
        title: {
        text: '',
        left: 'center',
        top: 0,
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold',
          color: '#333',
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          const productIndex = params[0].dataIndex;
          const product = products[productIndex];
          const avgMape = avgMapeData[productIndex];
          const highMapeRatio = productStats[productIndex].highMapeRatio;
          
          return `
            <div style="padding: 8px;">
              <div style="font-weight: bold; margin-bottom: 4px;">${product}</div>
              <div style="color: #3B82F6; margin-top: 4px;">
                MAPE平均值: <strong>${avgMape.toFixed(4)}</strong>
              </div>
              <div style="color: #10B981; margin-top: 4px;">
                MAPE>0.6月份比例: <strong>${(highMapeRatio * 100).toFixed(2)}%</strong>
              </div>
            </div>
          `;
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
        },
        padding: [8, 12],
        extraCssText: 'user-select: text; cursor: text;',
      },
      legend: {
        data: ['MAPE平均值', 'MAPE>0.6月份比例'],
        top: 50,
        left: 'center', // 将图例放在最左边，避免数据过多时看不到
        textStyle: {
          fontSize: 12,
        },
      },
      grid: {
        left: 80, // 固定左边距，留出Y轴空间
        right: 30, // 固定右边距
        bottom: 80, // 减少底部空间，不再需要滑块
        top: 100, // 为标题和图例留出空间
        containLabel: false, // 不自动包含标签，使用固定边距
      },
      // 添加数据缩放功能，支持鼠标滚轮和拖拽
      // 不设置start和end，让所有数据都渲染，通过滚动查看
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: [0],
          // 不设置start和end，渲染所有数据
          zoomOnMouseWheel: false, // 鼠标滚轮缩放
          moveOnMouseMove: false, // 鼠标拖拽平移
          moveOnMouseWheel: false, // 滚轮不移动，只缩放
          filterMode: 'none', // 不过滤数据，保持所有数据可访问
        },
      ],
      xAxis: {
        type: 'category',
        data: products,
        axisLabel: {
          rotate: 45,
          fontSize: 9,
          interval: 0, // 显示所有标签
          // 显示完整的产品编码
          formatter: (value: string) => {
            return value;
          },
        },
        axisLine: {
          lineStyle: {
            color: '#666',
          },
        },
      },
      yAxis: {
        type: 'value',
        name: 'MAPE值',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          fontSize: 8,
          fontWeight: 'bold',
        },
        min: 0,
        max: 5,
        interval: 0.5, // Y轴分隔间隔为0.5
        axisLabel: {
          formatter: (value: number) => value.toFixed(1),
          fontSize: 12,
        },
        axisLine: {
          lineStyle: {
            color: '#666',
          },
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: '#E5E7EB',
          },
        },
      },
      series: [
        {
          name: 'MAPE平均值',
          type: 'bar',
          data: avgMapeData,
          barWidth: barWidth, // 使用固定柱宽
          barCategoryGap: '5%', // 减小类别之间的间距，让柱状图更紧凑
          // 启用大数据量优化
          large: true,
          largeThreshold: 100, // 超过100个点时启用优化
          itemStyle: {
            color: '#3B82F6',
            borderRadius: [4, 4, 0, 0],
          },
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => {
              return params.value.toFixed(4);
            },
            fontSize: 8, // 减小字体以适应更多数据
            color: '#3B82F6',
          },
        },
        {
          name: 'MAPE>0.6月份比例',
          type: 'scatter',
          yAxisIndex: 0, // 使用同一个Y轴
          data: highMapeRatioData.map((ratio, index) => {
            // 将比例（0-1）转换为Y轴值（0-5范围）
            // 同时保存比例值，方便在标签中直接使用
            return {
              value: [index, ratio * 5],
              ratio: ratio, // 保存比例值
            };
          }),
          // 禁用大数据量优化，确保标签能正常显示
          large: false,
          // 禁用采样，确保所有数据点都显示
          sampling: false,
          itemStyle: {
            color: '#10B981',
          },
          symbolSize: 8, // 减小散点尺寸以适应更多数据
          label: {
            show: true,
            position: 'top', // 标签始终显示在散点上方
            formatter: (params: any) => {
              try {
                // 优先从data对象中直接获取ratio（最可靠的方式）
                if (params.data && typeof params.data === 'object' && 'ratio' in params.data) {
                  const ratio = params.data.ratio as number;
                  if (typeof ratio === 'number' && !isNaN(ratio)) {
                    // 显示比例百分比，保留两位小数
                    return `${(ratio * 100).toFixed(2)}%`;
                  }
                }
                
                // 如果data中没有ratio，尝试通过索引获取
                let index: number = -1;
                if (params.data && typeof params.data === 'object' && params.data.value && Array.isArray(params.data.value)) {
                  index = params.data.value[0];
                } else if (params.dataIndex !== undefined && params.dataIndex !== null) {
                  index = params.dataIndex;
                } else if (Array.isArray(params.value)) {
                  index = params.value[0];
                } else if (params.value && typeof params.value === 'object' && Array.isArray(params.value.value)) {
                  index = params.value.value[0];
                }
                
                // 从productStats获取比例
                if (index >= 0 && index < productStats.length) {
                  const ratio = productStats[index].highMapeRatio;
                  if (typeof ratio === 'number' && !isNaN(ratio)) {
                    return `${(ratio * 100).toFixed(2)}%`;
                  }
                }
                
                return '';
              } catch (e) {
                console.error('Label formatter error:', e, params);
                return '';
              }
            },
            fontSize: 8, // 减小字体以适应更多数据
            color: '#10B981',
            distance: 6, // 标签与散点的距离
          },
          // 标签布局避让 - 处理重叠问题
          labelLayout: {
            hideOverlap: true, // 数据多时隐藏重叠标签，避免显示过多
            moveOverlap: 'shiftY', // 在Y轴方向移动标签以避免重叠
            draggable: false,
          },
          // 启用emphasis，确保hover时标签可见
          emphasis: {
            label: {
              show: true,
            },
            itemStyle: {
              borderWidth: 2,
              borderColor: '#10B981',
            },
          },
        },
      ],
    };

       // 先设置option
       chart2Instance.current.setOption(option2, true); // 使用notMerge=true，完全替换配置

       // 再次等待DOM更新完成后再resize，确保ECharts能正确识别新的容器宽度
       requestAnimationFrame(() => {
         if (chart2Instance.current && chart2Ref.current) {
           // 强制触发浏览器重新计算布局
           const height = chart2Ref.current.offsetHeight;
           const width = chart2Ref.current.offsetWidth;
           console.log(`图表容器实际尺寸: ${width}px x ${height}px, 期望宽度: ${chartWidth}px`);
           
           // 如果实际宽度与期望宽度不一致，强制设置
           if (Math.abs(width - chartWidth) > 10) {
             console.warn(`宽度不匹配，强制设置宽度为 ${chartWidth}px`);
             chart2Ref.current.style.width = `${chartWidth}px`;
             chart2Ref.current.style.minWidth = `${chartWidth}px`;
             chart2Ref.current.style.maxWidth = `${chartWidth}px`;
             // 再次强制重排
             chart2Ref.current.offsetHeight; // 触发重排
           }
           
           // 调用resize让ECharts重新计算布局，使用实际容器尺寸
           const finalWidth = chart2Ref.current.offsetWidth;
           chart2Instance.current.resize({
             width: finalWidth, // 使用实际容器宽度
             height: height
           });
           
           console.log(`ECharts resize完成，宽度: ${finalWidth}px, 高度: ${height}px`);
           
           // 再次resize确保布局正确（有时需要两次resize）
           setTimeout(() => {
             if (chart2Instance.current && chart2Ref.current) {
               const currentWidth = chart2Ref.current.offsetWidth;
               const currentHeight = chart2Ref.current.offsetHeight;
               chart2Instance.current.resize({
                 width: currentWidth,
                 height: currentHeight
               });
               console.log(`二次resize完成，确保布局正确`);
             }
           }, 50);
         }
       });
      }); // 结束 requestAnimationFrame
    }); // 结束 setTimeout
  }; // 结束 updateChart2 函数

  // 数据变化时更新图表
  useEffect(() => {
    if (mapData.length > 0) {
      updateChart();
      updateChart2(); // 同时更新第二个图表
    }
  }, [mapData, isFullscreen]); // 添加 isFullscreen 依赖，全屏切换时重新渲染

  // 全屏模式变化时重新调整图表大小
  useEffect(() => {
    if (chartInstance.current) {
      setTimeout(() => {
        chartInstance.current?.resize();
      }, 100);
    }
    if (chart2Instance.current) {
      setTimeout(() => {
        chart2Instance.current?.resize();
      }, 100);
    }
  }, [isFullscreen]);

  // 清理图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
      if (chart2Instance.current) {
        chart2Instance.current.dispose();
      }
    };
  }, []);

  return (
    <div className={cn(
      "min-h-screen bg-gray-50",
      isFullscreen ? "fixed inset-0 z-50 bg-white" : ""
    )}>
      {/* 页面标题与筛选区 */}
      {!isFullscreen && (
        <div className="tech-hero-gradient mb-6" style={{ padding: "20px 24px" }}>
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0 flex-1">
              <Typography.Title level={4} className="tech-page-title" style={{ margin: 0, marginBottom: 6, fontSize: 20 }}>
                预测MAPE情况
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: 13, lineHeight: 1.6 }}>
                展示各产品编码在不同月份的MAPE值，MAPE大于0.5为异常值
              </Typography.Text>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <Select
                value={selectedBrandCode || undefined}
                onChange={handleBrandChange}
                disabled={loading}
                placeholder="请选择品牌"
                options={BRAND_OPTIONS.map((b) => ({ label: b.name, value: b.code }))}
                style={{ width: 160 }}
                allowClear={false}
              />
              <Input
                value={selectedProductCode}
                onChange={(e) => handleProductCodeChange(e.target.value)}
                placeholder="输入产品编码"
                disabled={loading}
                allowClear
                style={{ width: 200 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* 图表展示区域 */}
      <div className={cn(
        "px-6 pb-6",
        isFullscreen ? "p-4 h-screen" : ""
      )}>
        <div className={cn(
          "bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-blue-100",
          isFullscreen ? "h-full p-4" : "p-8"
        )}>
          {/* 头部标题和控制按钮 */}
          <div className="flex items-center justify-between">
            <h3 className={cn(
              "font-bold flex items-center gap-3 text-slate-800",
              isFullscreen ? "text-xl" : "text-2xl"
            )}>
              <BarChart3 className={cn("text-blue-600", isFullscreen ? "w-5 h-5" : "w-6 h-6")} />
              各产品MAPE趋势图
            </h3>

            {/* 控制按钮 */}
            <div className="flex items-center gap-2">
              {/* 缩放控制按钮 */}
              <div className="flex items-center bg-white rounded-lg shadow-md border border-gray-200">
                <button
                  onClick={zoomIn}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-l-lg transition-all duration-300"
                  title="放大"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={zoomOut}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-all duration-300"
                  title="缩小"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  onClick={resetZoom}
                  className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-r-lg transition-all duration-300"
                  title="重置视图"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>

              {/* 全屏控制按钮 */}
              <button
                onClick={toggleFullscreen}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200",
                  "text-gray-700 hover:text-gray-900"
                )}
              >
                {isFullscreen ? (
                  <>
                    <Minimize2 className="w-4 h-4" />
                    退出全屏
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-4 h-4" />
                    全屏显示
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 图表容器 */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
              <span className="ml-4 text-gray-600 text-lg">数据加载中...</span>
            </div>
          ) : mapData.length === 0 ? (
            <div className="text-center py-20 text-gray-500" style={{ height: `${isFullscreen ? window.innerHeight - 120 : 400}px` }}>
              <BarChart3 className="w-16 h-16 mx-auto mb-6 text-gray-300" />
              <p className="text-lg">暂无数据</p>
            </div>
          ) : (
            <div
              ref={chartRef}
              className={cn(
                "w-full transition-all duration-300"
              )}
              style={{ height: isFullscreen ? `${window.innerHeight - 200}px` : '1000px' }}
            />
          )}
        </div>
      </div>

      {/* 第二个图表：M+2预测情况 */}
      {!isFullscreen && mapData.length > 0 && (
        <div className="px-6 pb-6">
          <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-blue-100 p-8">
            {/* 头部标题 */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold flex items-center gap-3 text-slate-800 text-2xl">
                <BarChart3 className="text-blue-600 w-6 h-6" />
                M+2预测情况 (剔除上市前3个月、销售小于500)
              </h3>
            </div>

            {/* 第二个图表容器 - 支持水平滚动 */}
            <div className="w-full">
              {chart2ProductCount > 50 && (
                <div className="mb-2 text-sm text-gray-600 text-center">
                  共 {chart2ProductCount} 个产品，默认显示前 50 个，可通过左右滚动查看全部数据
                </div>
              )}
              <div
                className="w-full overflow-x-auto"
                style={{
                  maxWidth: '100%',
                  overflowY: 'hidden',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e1 #f1f5f9'
                }}
              >
                <div
                  ref={chart2Ref}
                  className="transition-all duration-300"
                  style={{ 
                    height: '600px', 
                    width: '100%', // 初始宽度100%，会根据计算出的chartWidth动态调整
                    minWidth: '100%' 
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapPredict;

